<script setup lang="ts">
import { reactive, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import Link from "@/dsComponents/buttons/Link.vue";

import EmptyState from "@/assets/illustrations/empty-state.svg";

const { t } = useI18n();

interface Props {
    modelValue?: string;
    accept?: string[];
    formatsHint?: string;
    maxSizeHint?: string;
}

const props = defineProps<Props>();
const emit = defineEmits(["update:modelValue", "droped"]);

const toast = useToast();

const input = useTemplateRef("input");

const meta = reactive({
    name: "",
    size: 0,
    type: "",
});

// Reset meta data when value is cleared
watch(
    () => props.modelValue,
    (value) => {
        if (!value) {
            reset();
        }
    }
);

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const onFileChange = (e: any) => {
    const fileList: FileList | undefined = e.target.files || e.dataTransfer?.files;
    if (!fileList?.length) return;

    const file = fileList[0];

    if (props.accept && !props.accept.some((extension) => file.name.includes(extension))) {
        toast.open({
            message: t("page.candidates.parsing.badExtension", {
                extensions: props.accept.map((extension) => extension.replace(".", "")).join(", "),
            }),
            type: "error",
            duration: 5000,
        });
        if (input.value) {
            input.value.value = "";
        }
        return;
    }

    createFile(file);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const createFile = (file: any) => {
    const reader: FileReader = new FileReader();

    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    reader.onload = (e: any) => {
        meta.name = file.name;
        meta.size = file.size;
        meta.type = file.type;
        emit("update:modelValue", e.target.result);
        emit("droped", e.target.result);
    };
    reader.readAsDataURL(file);
};

const focusInput = () => {
    input.value?.click();
};

const reset = () => {
    meta.name = "";
    meta.size = 0;
    meta.type = "";
    if (input.value) {
        input.value.value = "";
    }
};

defineExpose({
    focusInput,
    onFileChange,
    reset,
});
</script>

<template>
    <div class="dropzone" @click="focusInput" @drop.prevent="onFileChange" @dragover.prevent>
        <div v-if="!meta.name" class="dropzone__empty-state">
            <EmptyState class="dropzone__empty-state-illustration" />
            <span class="dropzone__empty-state-label typo-sm-medium">
                {{ `${t("global.actions.drop")} ${t("global.or")}` }}
                <Link :label="t('global.actions.selectFile')" type="neutral" size="sm" underline />
            </span>
            <div v-if="formatsHint || maxSizeHint" class="dropzone__empty-state-hints typo-xs-medium">
                <span v-if="formatsHint">{{ formatsHint }}</span>
                <span v-if="maxSizeHint">{{ maxSizeHint }}</span>
            </div>
        </div>
        <input ref="input" v-show="false" :accept="accept?.join(',')" type="file" @change="onFileChange" />
    </div>
</template>

<style lang="scss">
.dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: var(--spacing-3xl, 24px);
    border-radius: var(--radius-md, 8px);
    border: 1px dashed var(--stroke-sub-300, #acb1c3);
    background-color: var(--Neutral-Neutral-50, #f6f7f9);
    cursor: pointer;

    &__empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        &-label {
            display: flex;
            align-items: center;
            color: var(--text-main-900, #00061a);
        }

        &-illustration {
            width: 400px;
            height: auto;
        }

        &-hints {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--spacing-xs, 4px);
            padding-top: var(--spacing-xl, 16px);
            color: var(--text-sub-600, #494e61);
        }
    }
}
</style>
